import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/common/ui/Modal';
import { listProgressHistory as apiListHistory } from './api';
import type { WbsFlatItem } from './types';
import WbsSubmissionLogsModal from './WbsSubmissionLogsModal';
import { formatProjectDateOnly, formatProjectDateTime } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

type ApiHistoryRow = {
    weekOf: string; // ISO
    nodeId: string;
    progressId: string;

    statusForWeek: 'PENDING' | 'APPROVED' | 'REJECTED' | null;

    draftEV: number | null;
    submittedAt: string | null;

    approvedEV: number | null;
    approvedAt: string | null;

    rejectionNote: string | null;
    rejectedAt: string | null;
};

type UiHistoryRow = {
    weekISO: string; // YYYY-MM-DD
    progressId: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    ev: number | null; // 규칙에 따라 approvedEV 또는 draftEV
    submittedAt: string | null;
    finalizedAt: string | null; // approvedAt or rejectedAt
    summary: string; // 승인/반려/대기 요약
    rejectionNote: string | null;
};

function fmtDT(iso: string | null | undefined, zone: string) {
    return formatProjectDateTime(iso, zone, 'yyyy-MM-dd HH:mm', '—');
}

function statusBadge(status: UiHistoryRow['status']) {
    const base = 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]';
    if (status === 'PENDING') return `${base} bg-amber-100 text-amber-700`;
    if (status === 'APPROVED') return `${base} bg-emerald-100 text-emerald-700`;
    return `${base} bg-rose-100 text-rose-700`;
}

export default function WbsProgressHistoryModal({
    open,
    onClose,
    projectId,
    row,
}: {
    open: boolean;
    onClose: () => void;
    projectId: string;
    row: WbsFlatItem | null;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';
    const [raw, setRaw] = useState<ApiHistoryRow[]>([]);
    const [loading, setLoading] = useState(false);

    const [logsModal, setLogsModal] = useState<{
        open: boolean;
        submissionId: string | null;
        weekISO: string | null;
    }>({ open: false, submissionId: null, weekISO: null });

    useEffect(() => {
        if (!open || !row) return;

        setLoading(true);
        (async () => {
            try {
                const data = await apiListHistory(projectId, row.nodeId);
                // controller가 res.json({ ok:true, data }) 로 내리므로
                // 프론트 api.ts에서 (data?.data ?? data) 처리하고 있음 → 여기서는 그대로 배열 기대
                setRaw((data ?? []) as ApiHistoryRow[]);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, projectId, row, row?.nodeId]);

    useEffect(() => {
        if (!open) return;
        setLogsModal({ open: false, submissionId: null, weekISO: null });
    }, [open, row?.nodeId]);

    const items: UiHistoryRow[] = useMemo(() => {
        return (raw ?? []).map((s) => {
            const status = (s.statusForWeek ?? 'PENDING') as UiHistoryRow['status'];

            // ✅ EV 규칙:
            // - APPROVED: approvedEV
            // - PENDING/REJECTED: draftEV(현재 제출된 값)
            const ev = status === 'APPROVED' ? s.approvedEV : s.draftEV;

            // ✅ 확정 시각:
            // - APPROVED: approvedAt
            // - REJECTED: rejectedAt
            // - PENDING: null
            const finalizedAt = status === 'APPROVED' ? s.approvedAt : status === 'REJECTED' ? s.rejectedAt : null;

            const weekISO = formatProjectDateOnly(s.weekOf, zone, '');

            const summary =
                status === 'APPROVED'
                    ? '승인 완료'
                    : status === 'REJECTED'
                    ? s.rejectionNote
                        ? `반려: ${s.rejectionNote}`
                        : '반려'
                    : '검토 대기';

            return {
                weekISO,
                progressId: s.progressId,
                status,
                ev: typeof ev === 'number' ? ev : null,
                submittedAt: s.submittedAt ?? null,
                finalizedAt,
                summary,
                rejectionNote: s.rejectionNote ?? null,
            };
        });
    }, [raw, zone]);

    return (
        <>
            <Modal size="5xl" open={open} title="WBS 진행 히스토리" onClose={onClose}>
                {!row ? (
                    <div className="text-sm text-gray-500">선택된 항목이 없습니다.</div>
                ) : (
                    <div className="space-y-4">
                        {/* Header */}
                        <div className="rounded-xl border p-3">
                            <div className="text-xs text-gray-500">코드</div>
                            <div className="font-mono text-sm">{row.code}</div>
                            <div className="mt-2 text-base font-semibold">{row.name}</div>
                        </div>

                        {/* History */}
                        <div>
                            <div className="mb-2 text-sm font-medium">주차별 제출/승인/반려 요약</div>

                            <div className="rounded-xl border overflow-hidden">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500">
                                            <th className="px-3 py-2">주차</th>
                                            <th className="px-3 py-2 text-right">EV</th>
                                            <th className="px-3 py-2">제출 시각</th>
                                            <th className="px-3 py-2">상태</th>
                                            <th className="px-3 py-2">확정 시각</th>
                                            <th className="px-3 py-2">요약</th>
                                            <th className="px-3 py-2">로그</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                                                    불러오는 중…
                                                </td>
                                            </tr>
                                        ) : items.length === 0 ? (
                                            <tr>
                                                <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                                                    히스토리가 없습니다.
                                                </td>
                                            </tr>
                                        ) : (
                                            items.map((it) => (
                                                <tr key={it.progressId} className="border-t">
                                                    <td className="px-3 py-2 font-mono text-xs">{it.weekISO || '—'}</td>

                                                    <td className="px-3 py-2 text-right">
                                                        {typeof it.ev === 'number' ? (
                                                            `${it.ev}%`
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </td>

                                                    <td className="px-3 py-2">{fmtDT(it.submittedAt, zone)}</td>

                                                    <td className="px-3 py-2">
                                                        <span className={statusBadge(it.status)}>{it.status}</span>
                                                    </td>

                                                    <td className="px-3 py-2">{fmtDT(it.finalizedAt, zone)}</td>

                                                    <td className="px-3 py-2">
                                                        {it.status === 'REJECTED' ? (
                                                            <span className="text-rose-700">{it.summary}</span>
                                                        ) : it.status === 'APPROVED' ? (
                                                            <span className="text-emerald-700">{it.summary}</span>
                                                        ) : (
                                                            <span className="text-gray-700">{it.summary}</span>
                                                        )}
                                                    </td>

                                                    <td className="px-3 py-2">
                                                        <button
                                                            className="text-xs underline text-gray-600 hover:text-gray-900"
                                                            onClick={() =>
                                                                setLogsModal({
                                                                    open: true,
                                                                    submissionId: it.progressId,
                                                                    weekISO: it.weekISO,
                                                                })
                                                            }
                                                        >
                                                            보기
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="text-right">
                            <button className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50" onClick={onClose}>
                                닫기
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <WbsSubmissionLogsModal
                open={logsModal.open}
                onClose={() => setLogsModal({ open: false, submissionId: null, weekISO: null })}
                projectId={projectId}
                submissionId={logsModal.submissionId}
                fridayISO={logsModal.weekISO} // 기존 props 이름 유지(내부에서 표시용으로만 쓰면 OK)
            />
        </>
    );
}
